import type { RawTimeseriesPoint } from './types/internal-types.js';
import type { TimeseriesBand } from './types/timeseries.js';
import type {
  Timeseries,
  TimeseriesDatapoint,
} from '../../core/types/timeseries.js';
import { singleLineName } from '../../core/utils/single-line-name.js';
import type {
  XYChartSelectedDomains,
  XYChartSelectedSeries,
} from '../../xy-chart/types/xy-chart.js';

type AnySelectedItem = XYChartSelectedSeries[number][string][number];

const extractDatapoint = (item: AnySelectedItem): TimeseriesDatapoint => {
  const rawObj = 'datapoint' in item ? item.datapoint : item;
  const raw = rawObj as unknown as RawTimeseriesPoint;
  const datapoint: TimeseriesDatapoint = {
    start: raw.start,
    value: raw.value,
  };
  if (raw.end !== undefined) {
    datapoint.end = raw.end;
  }
  if (raw.center !== undefined) {
    datapoint.center = raw.center;
  }
  return datapoint;
};

export function mapSelectedSeriesToTimeseries(
  selectedSeries: XYChartSelectedSeries,
  originalSeries: (Timeseries | TimeseriesBand)[],
): Timeseries[] {
  return selectedSeries.flatMap((record) =>
    Object.entries(record)
      .filter(([, items]) => items.length > 0)
      .map(([seriesName, items]) => {
        const original = originalSeries.find(
          (s) => singleLineName(s.name) === seriesName,
        );
        return {
          ...(original ?? { name: seriesName }),
          datapoints: items.map(extractDatapoint),
        } as Timeseries;
      }),
  );
}

export function mapSelectionDomainToTimeseries(
  selectionDomain: XYChartSelectedDomains,
): [Date, Date] {
  // TimeseriesChart uses a single X-axis, so all series share the same domain — the first entry is sufficient.
  const [[, domain]] = Object.entries(selectionDomain[0]);
  return [domain[0] as Date, domain[1] as Date];
}
